// SPDX-License-Identifier: LGPL-3.0-or-later
// Copyright (c) 2025 James Prevett
/* eslint-disable @typescript-eslint/no-explicit-any */
import * as diff from './diff.js';
import { _throw } from './misc.js';
export function filterObject(object, predicate) {
    const entries = Object.entries(object);
    return Object.fromEntries(entries.filter(([key, value]) => predicate(key, value)));
}
export function pick(object, ...keys) {
    const picked = {};
    for (const key of keys.flat()) {
        picked[key] = object[key];
    }
    return picked;
}
export function omit(object, ...keys) {
    return filterObject(object, key => !keys.flat().includes(key));
}
export function assignWithDefaults(to, from, defaults = to) {
    const keys = new Set([...Object.keys(to), ...Object.keys(from)]);
    for (const key of keys) {
        try {
            to[key] = from[key] ?? defaults[key] ?? to[key];
        }
        catch {
            // Do nothing
        }
    }
}
/**
 * Returns whether `value` is not a primitive.
 *
 * This function is only useful for the type check,
 * you can do `Object(v) === v` otherwise.
 */
export function isObject(value) {
    return Object(value) === value;
}
export function deepAssign(to, from, treatArraysAsPrimitives = false) {
    const keys = new Set([
        ...Object.keys(to),
        ...Object.keys(from),
    ]);
    for (const key of keys) {
        if (!(key in from))
            continue;
        const value = from[key];
        if (!(key in to)) {
            to[key] = value;
            continue;
        }
        if ((!isObject(to[key]) && Object(value) !== value)
            || (treatArraysAsPrimitives && (Array.isArray(value) || Array.isArray(to[key])))) {
            to[key] = value;
            continue;
        }
        if (isObject(to[key]) && Object(value) === value) {
            deepAssign(to[key], value, treatArraysAsPrimitives);
            continue;
        }
        throw new TypeError(!isObject(to[key])
            ? 'Can not deeply assign an object to a primitive'
            : 'Can not deeply assign a primitive to an object');
    }
    return to;
}
export function structurallyEqual(to, from) {
    return (diff.deep(from, to).result === 'equal');
}
export function isJSON(str) {
    try {
        JSON.parse(str);
        return true;
    }
    catch {
        return false;
    }
}
export function resolveConstructors(object) {
    const constructors = [];
    for (let prototype = object; prototype && !['Function', 'Object'].includes(prototype.constructor.name); prototype = Object.getPrototypeOf(prototype)) {
        constructors.push(prototype.constructor.name);
    }
    return constructors;
}
export function* getAllPrototypes(object) {
    for (let prototype = object; prototype; prototype = Object.getPrototypeOf(prototype)) {
        yield prototype;
    }
}
export function map(items) {
    return new Map(Object.entries(items));
}
const keySeparator = /[.[\]]/;
/**
 * Get a value using a path of property keys.
 *
 * @example
 * Translation keys mapping to locale objects
 * ```ts
 * const locale = {
 * 	preference: {
 * 		theme: {
 * 			label: 'Theme'
 * 		}
 * 	}
 * } as const;
 *
 * const text = getByString(locale, 'preference.theme.label'); // 'Theme'
 * ```
 *
 * @example
 * Arrays
 * ```ts
 * const pascal = {
 * 	triangle: [
 * 		[1],
 * 		[1, 1],
 * 		[1, 2, 1],
 * 		[1, 3, 3, 1],
 * 		[1, 4, 6, 4, 1]
 * 	]
 * } as const;
 *
 * const row3 = getByString(pascal, 'triangle[3]'); // readonly [1, 3, 3, 1]
 * const r4c3 = getByString(pascal, 'triangle[4][2]'); // 6
 * ```
 */
export function getByString(object, path) {
    return path
        .toString()
        .split(keySeparator)
        .filter(p => p)
        .reduce((o, p) => (p == '__proto__' ? _throw(new Error('getByString called with __proto__ in path')) : o?.[p]), object);
}
export function setByString(object, path, value) {
    const parts = path
        .toString()
        .split(keySeparator)
        .filter(p => p);
    return parts.reduce((o, p, i) => {
        if (p == '__proto__')
            throw new Error('setByString called with __proto__ in path');
        return (o[p] = parts.length === i + 1 ? value : o[p] || {});
    }, object);
}
/**
 * Binds a this value for all of the functions in an object (not recursive)
 */
export function bindFunctions(fns, thisValue) {
    return Object.fromEntries(Object.entries(fns).map(([k, v]) => [k, typeof v == 'function' ? v.bind(thisValue) : v]));
}
